import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import UploadCard from "../../components/commons/UploadCard";
import StoryCarousel from "../../components/partials/story/StoryCarousel";
import StoryTextFields from "../../components/partials/story/StoryTextFields";
import Button from "../../components/commons/Button";
import type { Story, StoryDetail } from "../../services/webServices/srory/types";
import { getStories, getStoryById } from "../../services/webServices/srory/get";
import { postStory } from "../../services/webServices/srory/post";
import { patchStory } from "../../services/webServices/srory/patch";
import { deleteStory } from "../../services/webServices/srory/delete";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

export default function Story() {

  const [stories, setStories] = useState<Story[]>([]);
  const [editingStory, setEditingStory] =  useState<StoryDetail | null>(null);

  const [storyFile, setStoryFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  const [coverText, setCoverText] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [description, setDescription] = useState("");
  const [relatedContent, setRelatedContent] = useState("");

  const [formKey, setFormKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchStories = async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const data = await getStories();
    setStories(data);

    if (isFirstLoad) setIsLoading(false);
    hideLoading();
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const handleEdit = async (uuid: string) => {
    const data = await getStoryById({ uuid });
    if (!data) return;
    setEditingStory(data);
    setCoverText(data.title);
    setButtonText(data.label);
    setDescription(data.description);
    setRelatedContent(data.related_content ?? "");
    setStoryFile(null);
    setCoverFile(null);
    setFormKey((prev) => prev + 1);
  };

  const handleDelete = async (uuid: string) => {
    const success = await deleteStory({ uuid });

    if (!success) return;

    toast.success("استوری با موفقیت حذف شد");
    if (editingStory?.uuid === uuid) resetForm();
    fetchStories();
  };

  const resetForm = () => {
    setEditingStory(null);
    setStoryFile(null);
    setCoverFile(null);
    setCoverText("");
    setButtonText("");
    setDescription("");
    setRelatedContent("");
    setFormKey((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (
      !editingStory &&
      (!storyFile || !coverFile || !coverText || !buttonText || !description)
    ) {
      toast.info("لطفا همه فیلدها را تکمیل کنید");
      return;
    }

    setIsLoading(true);

    if (!editingStory) {
      const success = await postStory({
        storyFile: storyFile!,
        coverFile: coverFile!,
        coverText: coverText,
        buttonText: buttonText,
        description: description,
        related_content: relatedContent,
      });

      if (!success) return;

      toast.success("استوری با موفقیت ثبت شد");
    } else {
      const success = await patchStory({
        uuid: editingStory.uuid,
        originalStory: editingStory,
        storyFile: storyFile,
        coverFile: coverFile,
        coverText: coverText,
        buttonText: buttonText,
        description: description,
        related_content: relatedContent,
      });

      if (!success) return;

      toast.success("استوری با موفقیت ویرایش شد");
    }

    resetForm();
    fetchStories();
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <StoryCarousel
        story={stories}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <UploadCard
            key={`story-${formKey}`}
            title="محتوای اصلی استوری"
            description="این فایل به‌عنوان محتوای اصلی استوری نمایش داده می‌شود (عکس یا ویدیو)."
            helperText="عکس: JPG, PNG — ویدیو: MP4, WebM"
            mode="mixed"
            cropAspect={9 / 16}
            initialValue={
              editingStory
                ? {
                    type: editingStory.content.endsWith("webm")
                      ? "video"
                      : "image",
                    previewUrl:
                      process.env.REACT_APP_File_URL + editingStory.content,
                  }
                : undefined
            }
            onChange={setStoryFile}

          />

          <UploadCard
            key={`cover-${formKey}`}
            title="کاور استوری"
            description="این تصویر به‌عنوان کاور استوری در لیست استوری‌ها نمایش داده می‌شود."
            helperText="تنها فرمت‌های JPG و PNG پشتیبانی می‌شوند"
            mode="image"
            cropAspect={1}
            initialValue={
              editingStory
                ? {
                    type: "image",
                    previewUrl:
                      process.env.REACT_APP_File_URL +
                      editingStory.cover_image,
                  }
                : undefined
            }
            onChange={setCoverFile}
          />
        </div>

        <StoryTextFields
          cover={coverText}
          button={buttonText}
          relatedContent={relatedContent}
          description={description}
          onCoverChange={setCoverText}
          onButtonChange={setButtonText}
          onRelatedContentChange={setRelatedContent}
          onDescriptionChange={setDescription}
        />

        <div className="flex justify-end gap-4">
          <Button
            variant="gray"
            style="solid"
            size="lg"
            text="انصراف"
            loading={isLoading}
            disabled={false}
            onClick={resetForm}
          />

          <Button
            variant="primary"
            style="solid"
            size="lg"
            text={editingStory ? "ویرایش" : "ارسال"}
            loading={isLoading}
            disabled={false}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};